import { makeStyles } from '@material-ui/styles';
import { Tabs } from 'antd';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { bookingJourneyType } from 'common/constants/booking';
import BookingTable from './booking-table';
import BookingStageAndPropertyNameFilters from './booking-stage-and-property-name-filters';
import Export from './export';

const useStyles = makeStyles(theme => ({
  wrapper: {
    minHeight: 300,

    '& .ant-tabs-bar': {
      margin: '0',
    },

    '& .ant-tabs-tab': {
      padding: '0 24px !important',
      marginRight: '4px !important',

      '&.ant-tabs-tab-active': {
        position: 'relative',
        color: `${theme.colors.titleColor} !important`,
        ...theme.typography.h5,
      },

      '&:hover': {
        color: `${theme.colors.titleColor} !important`,
      },
    },
  },
  tables: {
    padding: '16px 24px',
    borderRadius: `0 0 ${theme.borderRadius.small}px ${theme.borderRadius.small}px`,
    backgroundColor: theme.colors.gray1,
    border: `1px solid ${theme.colors.borderColor}`,
    borderTop: 'none',
  },
}));

const BookingTableTabs = ({
  className,
  bookingType,
  setBookingType,
  bookingStage,
  setBookingStage,
  otherTableFilter,
  setOtherTableFilter,
  statisticsData,
  tableData,
  tableLoading,
  refetchTableData,
  pendingOrderCount,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const isHerculesBooking = useMemo(
    () => bookingType === bookingJourneyType.SEMI_AUTOMATIC,
    [bookingType],
  );

  const isInstantBooking = useMemo(
    () => bookingType === bookingJourneyType.INSTANT_BOOK,
    [bookingType],
  );
  const isLeadBooking = useMemo(
    () => bookingType === bookingJourneyType.LEAD_GENERATION,
    [bookingType],
  );

  const enableTabLength = useMemo(() => {
    return [
      statisticsData?.company?.herculesAllOrders?.totalCount,
      statisticsData?.company?.manualAllBookings?.totalCount,
      statisticsData?.company?.instantAllOrders?.totalCount,
      statisticsData?.company?.leadGenAllOrders?.totalCount,
    ].filter(item => !!item).length;
  }, [statisticsData?.company]);

  const handleClickTab = type => {
    setBookingType(type);
  };

  return (
    <div className={classNames(classes.wrapper, className)}>
      <If condition={enableTabLength >= 1}>
        <Tabs onChange={handleClickTab} type="card" activeKey={bookingType}>
          <If condition={statisticsData?.company?.leadGenAllOrders?.totalCount > 0}>
            <Tabs.TabPane
              tab={t('landlord.long_tail.booking.list.lead_gen.title')}
              key={bookingJourneyType.LEAD_GENERATION}
            />
          </If>
          <If condition={statisticsData?.company?.herculesAllOrders?.totalCount > 0}>
            <Tabs.TabPane
              tab={t('landlord.long_tail.booking.list.hercules_booking_title')}
              key={bookingJourneyType.SEMI_AUTOMATIC}
            />
          </If>
          <If condition={statisticsData?.company?.instantAllOrders?.totalCount > 0}>
            <Tabs.TabPane
              tab={t('landlord.long_tail.booking.list.instant_bookings')}
              key={bookingJourneyType.INSTANT_BOOK}
            />
          </If>

          <If condition={statisticsData?.company?.manualAllBookings?.totalCount > 0}>
            <Tabs.TabPane
              tab={t('landlord.long_tail.booking.list.manual_booking_title')}
              key={bookingJourneyType.MANUAL}
            />
          </If>
        </Tabs>
      </If>
      <div className={classes.tables}>
        <If condition={!isLeadBooking}>
          <BookingStageAndPropertyNameFilters
            setBookingStage={setBookingStage}
            bookingStage={bookingStage}
            bookingType={bookingType}
            pendingConfirmationOrderCount={pendingOrderCount.confirmation}
            pendingRefundOrderCount={pendingOrderCount.refund}
          />
        </If>
        <If condition={isLeadBooking}>
          <div className="leadgen_filter">
            <div>Total {tableData?.viewer?.company?.orders?.totalCount || 0} results</div>
            <Export className="leadgen_filter_export" />
          </div>
        </If>

        <BookingTable
          tableData={tableData}
          loading={tableLoading}
          isLeadBooking={isLeadBooking}
          isHerculesBooking={isHerculesBooking}
          isInstantBooking={isInstantBooking}
          bookingJourney={bookingType}
          otherTableFilter={otherTableFilter}
          setOtherTableFilter={setOtherTableFilter}
          refetchData={refetchTableData}
        />
      </div>
    </div>
  );
};

BookingTableTabs.propTypes = {
  className: PropTypes.string,
  bookingType: PropTypes.string,
  setBookingType: PropTypes.func,
  bookingStage: PropTypes.string,
  setBookingStage: PropTypes.func,
  otherTableFilter: PropTypes.object,
  setOtherTableFilter: PropTypes.func,
  tableData: PropTypes.object,
  tableLoading: PropTypes.bool,
  refetchTableData: PropTypes.func,
  pendingOrderCount: PropTypes.object,
  statisticsData: PropTypes.object,
};

export default BookingTableTabs;
